import React, { useRef, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Export, UploadSimple, Warning } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

const SUPPORTED = [
  { key: 'business_tracker', label: 'Business Tracker' },
  { key: 'priorities_tasks', label: 'Priorities / Roadmap' },
  { key: 'partners', label: 'Partners' },
  { key: 'affiliate_products', label: 'Affiliate Products' },
];

/**
 * Admin Data Migration Card
 * Export admin-curated datasets from this environment to a JSON file, then
 * upload that file to another environment (e.g. preview → production) via
 * the Import side. User data is deliberately excluded from the allowlist.
 */
export const DataMigrationCard = () => {
  const [selected, setSelected] = useState(['business_tracker', 'priorities_tasks']);
  const [busy, setBusy] = useState(false);
  const [lastImport, setLastImport] = useState(null);
  const fileRef = useRef(null);

  const toggle = (key) => {
    setSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleExport = async () => {
    if (selected.length === 0) { toast.error('Pick at least one collection'); return; }
    setBusy(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/data-export?collections=${selected.join(',')}`,
        { withCredentials: true }
      );
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.href = url;
      a.download = `turfpulse-export-${res.data.source_environment || 'env'}-${stamp}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${Object.keys(res.data.collections || {}).length} collections`);
    } catch {
      toast.error('Export failed');
    } finally { setBusy(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const res = await axios.post(
        `${API_URL}/api/admin/data-import`,
        payload,
        { withCredentials: true }
      );
      setLastImport(res.data);
      const total = Object.values(res.data.summary || {}).reduce((s, v) => s + (v.upserted || 0), 0);
      toast.success(`Imported ${total} records into ${res.data.target_environment}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed — check file shape');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Card className="mt-6" data-testid="data-migration-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Export weight="bold" size={18} className="text-blue-600 dark:text-blue-400" />
          Environment Migration
        </CardTitle>
        <CardDescription>
          Move admin-curated data between preview and production. User activity and account data are intentionally excluded.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Collections</div>
          <div className="flex flex-wrap gap-2">
            {SUPPORTED.map(({ key, label }) => {
              const active = selected.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`mig-chip-${key}`}
                  onClick={() => toggle(key)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-transparent text-muted-foreground border-border hover:border-blue-400'}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t">
          <Button
            onClick={handleExport}
            disabled={busy || selected.length === 0}
            data-testid="export-data-btn"
            variant="outline"
            className="w-full"
          >
            <Export weight="bold" size={14} className="mr-2" />
            Export to JSON
          </Button>
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            data-testid="import-data-btn"
            className="w-full"
          >
            <UploadSimple weight="bold" size={14} className="mr-2" />
            Import from JSON
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json"
            onChange={handleImport}
            className="hidden"
          />
        </div>

        <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/40 rounded-lg p-2">
          <Warning weight="fill" size={14} className="mt-0.5 shrink-0" />
          <span>Import UPSERTS by id — rows with matching ids are overwritten. Rows not in the import are kept as-is (never silently deleted).</span>
        </div>

        {lastImport && (
          <div className="text-xs border-t pt-3" data-testid="last-import-summary">
            <div className="font-semibold uppercase tracking-wide text-muted-foreground mb-1">Last import → {lastImport.target_environment}</div>
            {Object.entries(lastImport.summary || {}).map(([name, r]) => (
              <div key={name} className="flex justify-between">
                <span className="font-mono">{name}</span>
                <span className={r.upserted ? 'text-emerald-600' : 'text-muted-foreground'}>
                  {r.upserted ?? 0} upserted{r.skipped ? `, ${r.skipped} skipped` : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
